// src/components/programs/ProgramForm/Step2Sections/DayQuotationList.jsx

import React, { useMemo } from "react";
import { Box, Typography, Alert, Stack, Chip, alpha, useTheme } from "@mui/material";
import {
    WarningAmber as WarningIcon,
    Inventory2Outlined as ProductIcon,
} from "@mui/icons-material";
import ProductQuotationCard from "./DayQuotation/ProductQuotationCard";
import HotelQuotationCard from "./DayQuotation/HotelQuotationCard";
import { getProductTypeGroup } from "../helpers/quotationCalculations";
import { PRICING_CODES } from "../helpers/formConstants";

const DayQuotationList = ({
    formData,
    onUpdateProductPricing,
    onUpdateHotelPricing,
    errors,
    isArabic,
    activeLang,
}) => {
    const theme = useTheme();

    const paxTypesInfo = formData.pax_types || [];
    const totalPax = paxTypesInfo.reduce((sum, pt) => sum + (pt.count || 0), 0) || 0;

    const mergedProducts = useMemo(() => {
        const productsMap = new Map();

        formData.itinerary?.forEach((day, dayIndex) => {
            day.products?.forEach((product, productIndex) => {
                const productId = product.product_snapshot?.product_id;
                const pricingUnit = product.product_snapshot?.pricing_unit;

                if (!productId) return;
                
                const typeCode = product.product_snapshot?.productType?.code || product.product_snapshot?.productType ||
                    product.product_snapshot?.product_type?.code || product.product_snapshot?.product_type ||
                    product.productType?.code || product.productType ||
                    product.product_type?.code || product.product_type;
                    
                const isNonPriced = pricingUnit === PRICING_CODES.FREE_DAY || 
                                    pricingUnit === PRICING_CODES.FREE_TIME || 
                                    pricingUnit === PRICING_CODES.PROGRAM_MARKER ||
                                    typeCode === 'free_day' || 
                                    typeCode === 'free_time';

                if (isNonPriced) return;

                const pricingBehavior = product.pricing_behavior || product.product_snapshot?.pricing_behavior;
                const typeGroup = getProductTypeGroup(product);

                let groupingKey = productId;
                if (typeGroup === 'flight' && pricingBehavior === 'per_occurrence') {
                    groupingKey = `${productId}_day${dayIndex}_prod${productIndex}`;
                }

                if (!productsMap.has(groupingKey)) {
                    productsMap.set(groupingKey, {
                        ...product,
                        locations: [{ dayIndex, productIndex }],
                        merged_quantities: {
                            units: pricingUnit === 'per_unit' ? (product.dynamic_fields?.units || 0) : 0,
                            nights: pricingUnit === 'per_night' ? (product.dynamic_fields?.nights || 0) : 0,
                            persons_count: pricingUnit === 'per_person' ? 1 : 0,
                        },
                        repetition_count: 1,
                    });
                } else {
                    const existingProduct = productsMap.get(groupingKey);
                    existingProduct.locations.push({ dayIndex, productIndex });

                    if (pricingUnit === 'per_unit') {
                        existingProduct.merged_quantities.units += (product.dynamic_fields?.units || 0);
                    } else if (pricingUnit === 'per_night') {
                        existingProduct.merged_quantities.nights += (product.dynamic_fields?.nights || 0);
                    } else if (pricingUnit === 'per_person') {
                        existingProduct.merged_quantities.persons_count += 1;
                    }

                    existingProduct.repetition_count += 1;
                }
            });
        });

        return Array.from(productsMap.values());
    }, [formData.itinerary]);

    const productCounts = useMemo(() => {
        const counts = { tour: 0, transfer: 0, flight: 0, other: 0 };
        const otherNames = new Set();
        mergedProducts.forEach(p => {
            const typeGroup = getProductTypeGroup(p);
            
            if (counts[typeGroup] !== undefined) {
                counts[typeGroup] += 1;
            } else {
                counts['other'] += 1;
            }
        });
        return { counts, otherNames: Array.from(otherNames).join(', ') };
    }, [mergedProducts]);

    const hotels = formData.hotels || [];

    const hasContent = mergedProducts.length > 0 || hotels.length > 0;

    if (!hasContent) {
        return (
            <Alert
                severity="warning"
                icon={<WarningIcon sx={{ fontSize: 18 }} />}
                sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    '& .MuiAlert-icon': { fontSize: 18, mr: 1 },
                    '& .MuiAlert-message': { padding: 0 },
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {isArabic
                        ? "لا توجد منتجات لحساب التسعير. الرجاء إضافة منتجات في الخطوة السابقة."
                        : "No products to calculate pricing. Please add products in the previous step."}
                </Typography>
            </Alert>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box
                        sx={{
                            width: 32, height: 32, borderRadius: 1.5,
                            bgcolor: theme.palette.info.main,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <ProductIcon sx={{ fontSize: 18, color: 'white' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                            {isArabic ? "المنتجات والإقامات الفندقية" : "Products & Hotel Stays"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                            {isArabic
                                ? `${mergedProducts.length} منتج + ${hotels.length} إقامة فندقية`
                                : `${mergedProducts.length} products + ${hotels.length} hotel stays`}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {productCounts.counts.tour > 0 && (
                            <Chip
                                label={`${productCounts.counts.tour} ${isArabic ? 'جولة' : 'Tours'}`}
                                size="small" color="info"
                                sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                        )}
                        {productCounts.counts.transfer > 0 && (
                            <Chip
                                label={`${productCounts.counts.transfer} ${isArabic ? 'انتقال' : 'Transfers'}`}
                                size="small" color="warning"
                                sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                        )}
                        {productCounts.counts.flight > 0 && (
                            <Chip
                                label={`${productCounts.counts.flight} ${isArabic ? 'طيران' : 'Flights'}`}
                                size="small" color="secondary"
                                sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                        )}
                        {productCounts.counts.other > 0 && (
                            <Chip
                                label={`${productCounts.counts.other} Others ${productCounts.otherNames}`}
                                size="small" color="default"
                                sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                        )}
                        {hotels.length > 0 && (
                            <Chip
                                label={`${hotels.length} ${isArabic ? 'فندق' : 'Hotels'}`}
                                size="small" color="secondary"
                                sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                        )}
                        <Chip
                            label={`${totalPax} ${isArabic ? 'راكب' : 'Pax'}`}
                            size="small" color="primary"
                            sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                        />
                    </Stack>
                </Stack>
            </Box>

            <Stack spacing={1.5}>
                {/* Hotel stays first */}
                {hotels.map((hotel, index) => (
                    <HotelQuotationCard
                        key={hotel.id || index}
                        hotel={hotel}
                        hotelIndex={index}
                        errors={errors}
                        isArabic={isArabic}
                        paxTypes={paxTypesInfo}
                        formData={formData}
                        onUpdateHotelPricing={onUpdateHotelPricing}
                    />
                ))}

                {/* Regular products */}
                {mergedProducts.map((product, index) => (
                    <ProductQuotationCard
                        key={product.instance_id || index}
                        product={product}
                        productIndex={index}
                        onUpdatePricing={onUpdateProductPricing}
                        errors={errors}
                        isArabic={isArabic}
                        activeLang={activeLang}
                        paxTypes={paxTypesInfo}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export default DayQuotationList;
